import { useState } from "react";
import { Siren, CalendarCheck, ChevronDown, ClipboardList, Stethoscope, FlaskConical, CheckCircle2, AlertTriangle, ShieldAlert, Hourglass, BookCheck, UserCheck } from "lucide-react";

const TIER = {
  proceed: { chip: "PROCEED", head: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", badge: "bg-emerald-600", Icon: CheckCircle2 },
  precautions: { chip: "PROCEED WITH PRECAUTIONS", head: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", badge: "bg-amber-500", Icon: AlertTriangle },
  clearance: { chip: "SPECIALIST APPROVAL REQUIRED", head: "bg-orange-50", border: "border-orange-400", text: "text-orange-700", badge: "bg-orange-600", Icon: Stethoscope },
  defer: { chip: "DEFER / DO NOT PERFORM", head: "bg-red-50", border: "border-red-300", text: "text-red-700", badge: "bg-red-600", Icon: ShieldAlert },
  pending: { chip: "INFORMATION NEEDED", head: "bg-slate-50", border: "border-slate-300", text: "text-slate-800", badge: "bg-slate-600", Icon: Hourglass },
};
const LEGACY = { green: "proceed", yellow: "precautions", orange: "clearance", red: "defer" };

function Steps({ items, tone, testid }) {
  return (
    <ol data-testid={testid} className="mt-2 space-y-2">
      {items.map((s, i) => (
        <li key={i} className="flex gap-2 text-sm text-slate-800 leading-relaxed">
          <span className={`shrink-0 w-5 h-5 rounded-full text-[11px] font-bold text-white flex items-center justify-center mt-0.5 ${tone}`}>{i + 1}</span>
          <span>{s.text}{s.source && <span className="text-[11px] text-slate-500"> · {s.source}</span>}</span>
        </li>
      ))}
    </ol>
  );
}

function Basis({ items }) {
  if (!items?.length) return null;
  return (
    <div data-testid="final-basis" className="px-6 py-4 border-b border-slate-200">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600"><BookCheck className="w-4 h-4" /> Evidence basis for this decision</p>
      <ul className="mt-2 space-y-1.5">
        {items.map((b, i) => (
          <li key={i} className="text-sm text-slate-800 leading-relaxed" data-testid={`final-basis-item-${i}`}>
            • {b.text}{b.ref && <span className="text-[11px] text-slate-500 italic"> — {b.ref}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Consult({ fd }) {
  const mandatory = fd.consult?.length > 0;
  const list = mandatory ? fd.consult : fd.optional_consult || [];
  if (!list.length) return null;
  const isDefer = fd.tier === "defer";
  return (
    <div data-testid={mandatory ? "final-consult" : "final-optional-consult"}
      className={`px-6 py-4 border-b border-slate-200 ${mandatory ? "bg-orange-50/60" : "bg-sky-50/50"}`}>
      <p className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${mandatory ? "text-orange-800" : "text-sky-800"}`}>
        {mandatory ? <Stethoscope className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
        {mandatory ? (isDefer ? "Refer with the report to" : "Mandatory — send the report FIRST to") : "Optional — to confirm, you may send the report to"}
      </p>
      <ul className="mt-2 space-y-1">
        {list.map((c) => <li key={c.specialist} className="text-sm text-slate-800">• <b>{c.specialist}</b> <span className="text-slate-500">— {c.for.join(", ")}</span></li>)}
      </ul>
      <p className="text-xs text-slate-500 mt-2">
        {mandatory ? (isDefer ? "For optimisation / management, then re-assess." : "One report for all specialists. Do not start until approval is received — then record it in Pending Cases.")
          : "Not required by the guidelines — the decision above already stands. Use only if you want a second confirmation."}
      </p>
    </div>
  );
}

export function FinalDecision({ fd, procedureName }) {
  if (!fd) return null;
  const t = TIER[fd.tier] || TIER[LEGACY[fd.status]] || TIER.proceed;
  const pending = fd.tier === "pending";
  return (
    <div data-testid="final-decision" data-tier={fd.tier} className={`rounded-2xl border-2 ${t.border} bg-white overflow-hidden shadow-sm`}>
      <div className={`${t.head} px-6 py-5 flex items-start gap-3`}>
        <div className={`w-11 h-11 rounded-xl ${t.badge} flex items-center justify-center shrink-0`}><t.Icon className="w-6 h-6 text-white" /></div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Final decision · {procedureName}</p>
          <span data-testid="final-tier-chip" className={`inline-block mt-1 rounded-full ${t.badge} text-white text-[11px] font-bold tracking-wide px-2.5 py-0.5`}>{t.chip}</span>
          <p className={`font-heading text-lg sm:text-xl font-bold ${t.text} leading-snug mt-1.5`} data-testid="final-verdict">{fd.verdict}</p>
          <p className="text-sm text-slate-700 mt-1" data-testid="final-reason">{fd.reason}</p>
        </div>
      </div>
      {fd.before?.length > 0 && (
        <div data-testid="final-before" className="px-6 py-4 bg-amber-50 border-y border-amber-200 text-sm text-amber-900">
          <p className="flex items-center gap-2 font-bold uppercase tracking-wider text-xs"><ClipboardList className="w-4 h-4" /> Required first — before the routine decision</p>
          <ol className="mt-2 space-y-1">
            {fd.before.map((b, i) => <li key={i} data-testid={`final-before-item-${i}`}>{i + 1}. <b>{b.label}</b>{b.reason && <span className="text-amber-800/80"> — {b.reason}</span>}</li>)}
          </ol>
          <p className="text-xs mt-2 text-amber-800">Enter the answers / results in the follow-up section and re-assess. The routine plan and any referral appear only after this.</p>
        </div>
      )}
      <Basis items={fd.basis} />
      {fd.labs?.some((l) => l.status === "entered") && (
        <div data-testid="final-labs" className="px-6 py-3 border-b border-slate-200 text-sm text-slate-700 flex gap-2 flex-wrap items-center">
          <FlaskConical className="w-4 h-4 text-sky-600" /><b>Results:</b>
          {fd.labs.filter((l) => l.status === "entered").map((l) => (
            <span key={l.id} className="rounded-full bg-sky-50 border border-sky-200 px-2.5 py-0.5 text-xs">{l.name.split(" — ").pop()}: <b>{l.value} {l.unit}</b></span>
          ))}
        </div>
      )}
      <Consult fd={fd} />
      <div className={`grid gap-0 md:divide-x divide-slate-200 ${pending ? "" : "md:grid-cols-2"}`}>
        <div className="p-5 bg-red-50/60" data-testid="final-emergency">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-700"><Siren className="w-4 h-4" /> If EMERGENCY — do exactly this{fd.emergency_gated ? " (information required first)" : ""}</p>
          <Steps items={fd.emergency} tone="bg-red-600" testid="final-emergency-steps" />
        </div>
        {!pending && <div className="p-5 bg-emerald-50/60" data-testid="final-routine">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700"><CalendarCheck className="w-4 h-4" /> If ROUTINE — do exactly this</p>
          <Steps items={fd.routine} tone="bg-emerald-600" testid="final-routine-steps" />
        </div>}
      </div>
    </div>
  );
}

export function DetailsToggle({ count, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4">
      <button type="button" onClick={() => setOpen(!open)} data-testid="toggle-details-btn"
        className="w-full flex items-center justify-between rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors">
        <span>{open ? "Hide" : "Show"} full details, explanation & references ({count} item{count === 1 ? "" : "s"})</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="space-y-3 mt-3" data-testid="details-content">{children}</div>}
    </div>
  );
}
